import {
  createFeatureSelector,
  createSelector,
  MemoizedSelector
} from '@ngrx/store';
import { GamedayActionUnion, GamedayActionTypes } from './actions';
import {
  calculateData,
  calculateDataWithAppliedLegends,
  filterGameDayData,
  searchByManagerAccount
} from '../../gameday/data/gameDay-data.calculation';
import { GameDayModel } from '../../models/gameday-model';
import * as fromReports from '../reports/reducer';
import * as fromMonitor from '../monitor/reducer';
import { GameDayReducerState } from '../gameday.state';
import {
  AccountManager,
  GameDayViewType,
  GamedayFiltersState,
  GamedaySettingsState,
  GamedayState
} from './state';
import {
  DealWithTrancheOrders,
  FilteredTranches,
  FilteredTranchesWithFilters
} from '../types';

const defaultGamedayFiltersState: GamedayFiltersState = {
  viewType: GameDayViewType.PriorityType,
  seriesId: undefined,
  ignoreStocks: false,
  isSealedBid: false,
  legendCategories: [],
  searchByAccountManager: undefined
};

const defaultGamedaySettingsState: GamedaySettingsState = {
  isAutoUpdateTurnedOn: undefined,
  showMarketDrawers: false
};

export const defaultGamedayState: GamedayState = {
  deal: undefined,
  orders: undefined,
  filters: defaultGamedayFiltersState,
  settings: defaultGamedaySettingsState,
  isLoading: true
};

export function gamedayDataReducer(
  state: GamedayState = defaultGamedayState,
  action: GamedayActionUnion
): GamedayState {
  switch (action && action.type) {
    case GamedayActionTypes.SetRawData:
      return {
        ...state,
        ...(action.payload as GameDayModel.RawData),
        settings: {
          ...state.settings,
          isAutoUpdateTurnedOn: true
        },
        isLoading: false
      };

    case GamedayActionTypes.SetViewType:
      return {
        ...state,
        filters: {
          ...state.filters,
          viewType: action.payload as GameDayViewType
        }
      };

    case GamedayActionTypes.SetFilters:
      return {
        ...state,
        filters: {
          ...state.filters,
          ...(action.payload as GamedayFiltersState)
        }
      };

    case GamedayActionTypes.SetSearchBy:
      return {
        ...state,
        filters: {
          ...state.filters,
          searchByAccountManager: action.payload as AccountManager
        }
      };

    case GamedayActionTypes.ShowMarketDrawers:
      return {
        ...state,
        settings: {
          ...state.settings,
          showMarketDrawers: action.payload as boolean
        }
      };

    case GamedayActionTypes.ResetData:
      return defaultGamedayState;

    default:
      return state;
  }
}
export const selectGamedayState: MemoizedSelector<
  { gameDay: GameDayReducerState },
  GameDayReducerState
> = createFeatureSelector<
  { gameDay: GameDayReducerState },
  GameDayReducerState
>('gameDay');

export const getGamedayState = createSelector(
  selectGamedayState,
  (state: GameDayReducerState) => state
);

export const getGamedayDataState = createSelector(
  getGamedayState,
  (state: GameDayReducerState): GamedayState => state.data
);

export const getGamedayFiltersState = createSelector(
  getGamedayDataState,
  (state: GamedayState): GamedayFiltersState => state.filters
);

export const getGamedaySettingsState = createSelector(
  getGamedayDataState,
  (state: GamedayState): GamedaySettingsState => state.settings
);

export const getGamedayLoadingState = createSelector(
  getGamedayDataState,
  (state: GamedayState): boolean => state.isLoading
);

export const getGamedayViewTypeState = createSelector(
  getGamedayFiltersState,
  (state: GamedayFiltersState) => state.viewType
);

export const getGamedayDealState = createSelector(
  getGamedayDataState,
  (state: GamedayState) => state.deal
);

export const getOrdersState = createSelector(
  getGamedayDataState,
  (state: GamedayState) => state.orders
);

export const getSearchByAccountManagerState = createSelector(
  getGamedayFiltersState,
  (state: GamedayFiltersState) => state.searchByAccountManager
);

export const getDealWithOrdersState = createSelector(
  getGamedayDealState,
  getOrdersState,
  (deal, orders): GameDayModel.RawData => ({ deal, orders })
);

export const getChartsState = createSelector(
  selectGamedayState,
  (state: GameDayReducerState) => state.charts
);

export const getNegativeCasesState = createSelector(
  selectGamedayState,
  (state: GameDayReducerState) => state.negativeCases
);

export const getProfileState = createSelector(
  selectGamedayState,
  (state: GameDayReducerState) => state.profile
);

export const getMonitorState = createSelector(
  selectGamedayState,
  (state: GameDayReducerState) => state.monitor
);

export const getMonitorViewTypeState = createSelector(
  getMonitorState,
  fromMonitor.getViewType
);

export const getMonitorSelectedMaturityState = createSelector(
  getMonitorState,
  fromMonitor.getSelectedMaturity
);

export const getMonitorCompanyTypeFilterState = createSelector(
  getMonitorState,
  fromMonitor.getCompanyTypeFilter
);

export const getMonitorMemberPriorityFilterState = createSelector(
  getMonitorState,
  fromMonitor.getMemberPriorityFilter
);

export const getMonitorRetailPriorityFilterState = createSelector(
  getMonitorState,
  fromMonitor.getRetailPriorityFilter
);

export const getReportsState = createSelector(
  selectGamedayState,
  (state: GameDayReducerState) => state.reports
);

export const getReportsActiveTabState = createSelector(
  getReportsState,
  fromReports.getActiveTabState
);

export const getTrancheOrdersState = createSelector(
  getGamedayDataState,
  (data: GamedayState): FilteredTranches => {
    if (!data.deal || !data.orders) {
      const defaultFilteredSearchedData = {
        deal: data.deal,
        orders: data.orders,
        segments: undefined,
        segmentsMap: undefined,
        trancheGroups: undefined,
        trancheGroupsMap: undefined
      };

      return {
        tranches: {
          filtered: defaultFilteredSearchedData,
          searched: defaultFilteredSearchedData
        }
      };
    }

    const filteredGameDayData: GameDayModel.RawData = filterGameDayData(
      { deal: data.deal, orders: data.orders },
      data.filters
    );

    const filteredData: DealWithTrancheOrders = calculateData(
      filteredGameDayData,
      data.filters.viewType
    );

    const searchedGameDayData: GameDayModel.RawData = searchByManagerAccount(
      filteredGameDayData,
      data.filters.searchByAccountManager
    );

    const searchedData: DealWithTrancheOrders = calculateDataWithAppliedLegends(
      searchedGameDayData,
      data.filters.viewType,
      data.filters.legendCategories
    );

    return {
      tranches: {
        filtered: filteredData,
        searched: searchedData
      }
    };
  }
);

export const getTrancheOrdersFilteredState = createSelector(
  getTrancheOrdersState,
  (state: FilteredTranches): DealWithTrancheOrders =>
    state.tranches.filtered
);

export const getTrancheOrdersSearchedState = createSelector(
  getTrancheOrdersState,
  (state: FilteredTranches): DealWithTrancheOrders =>
    state.tranches.searched
);

export const getTrancheOrdersFilteredWithFiltersState = createSelector(
  getTrancheOrdersState,
  getGamedayFiltersState,
  (
    state: FilteredTranches,
    filters: GamedayFiltersState
  ): FilteredTranchesWithFilters => ({
    tranches: state.tranches,
    filters
  })
);

export const getFullGamedayState = createSelector(
  getGamedayState,
  getTrancheOrdersState,
  (
    { 
      data,
      monitor,
      negativeCases,
      profile,
      reports,
      charts
    },
    { tranches }
  ) => ({
    data,
    profile,
    monitor,
    reports,
    charts,
    negativeCases,
    tranches
  })
);

// Need these lines for GOD KNOWS WHY
// Need these lines for GOD KNOWS WHY
// Need these lines for GOD KNOWS WHY
// Need these lines for GOD KNOWS WHY
// Need these lines for GOD KNOWS WHY
// Need these lines for GOD KNOWS WHY
// Need these lines for GOD KNOWS WHY
// Need these lines for GOD KNOWS WHY
// Need these lines for GOD KNOWS WHY
// Seriously, deleting these breaks webpack because it adds undefinedundefinedundefined... over and over. 
// BIZARE